import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner, Button, EmptyState, IconButton, Panel, SimpleTable, Spinner, Tag, ckConfirm, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiDataEntryApi, type DataEntrySessionSummaryDto } from "../../../api/aiDataEntryApi";
import { ScreenHeader } from "../../page/ScreenHeader";
import DataEntryAgent from "./DataEntryAgent";
import { IMPORT_TYPE_OPTIONS } from "./dataEntryModel";
import "./dataEntry.css";

/**
 * /ai/data-entry (ADR-0037): the agent itself is a sheet that opens from anywhere (Alt+6, the quick
 * actions); this page opens it and lists my recent files — `?session=<id>` reopens one where it was.
 */
const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

export function DataEntryAgentPage() {
  const toast = useCkToast();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session");
  const [agent, setAgent] = useState<{ sessionId: string | null } | null>(sessionId ? { sessionId } : null);
  const [recent, setRecent] = useState<DataEntrySessionSummaryDto[] | null>(null);

  const load = useCallback(() => {
    aiDataEntryApi
      .sessions()
      .then(setRecent)
      .catch(() => setRecent([]));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (sessionId) setAgent({ sessionId });
  }, [sessionId]);

  const openSession = (id: string | null) => {
    setParams(id ? { session: id } : {}, { replace: false });
    setAgent({ sessionId: id });
  };
  const closeAgent = () => {
    setAgent(null);
    if (sessionId) setParams({}, { replace: true });
    load();
  };

  const remove = async (id: string) => {
    if (!(await ckConfirm({ title: "מחיקת הקובץ מהרשימה", message: "רשומות שכבר נרשמו נשארות במערכת.", confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await aiDataEntryApi.remove(id);
      setRecent((r) => (r ? r.filter((x) => x.id !== id) : r));
    } catch (e) {
      toast.error(errorText(e, "המחיקה נכשלה"));
    }
  };

  return (
    <div className="de-page">
      <ScreenHeader
        sub="מקובץ Excel או CSV לרשומות במערכת — הסוכן מזהה, משייך ומתריע, אתם מאשרים, וכל שורה ניתנת לביטול"
        primary={{ label: "קובץ חדש", icon: "upload", onClick: () => openSession(null) }}
        helpSlug="ai-data-entry"
      />
      <Banner tone="info" icon="sparkle">
        אפשר לפתוח את ההזנה החכמה מכל מסך — בכפתורי «חדש» שבסרגל העליון או ב-<b>Alt+6</b>.
      </Banner>
      <Panel title="קבצים אחרונים" icon="history" flush>
        {recent === null ? (
          <div className="de__center">
            <Spinner size={18} />
          </div>
        ) : recent.length === 0 ? (
          <EmptyState icon="upload" title="עוד לא הועלו קבצים" action={<Button variant="primary" icon="upload" onClick={() => openSession(null)}>העלאת קובץ</Button>} compact />
        ) : (
          <SimpleTable
            ariaLabel="קבצים אחרונים"
            rows={recent}
            rowKey={(r: DataEntrySessionSummaryDto) => r.id}
            onRowClick={(r: DataEntrySessionSummaryDto) => openSession(r.id)}
            columns={[
              { key: "fileName", label: "קובץ" },
              { key: "importType", label: "סוג", render: (r: DataEntrySessionSummaryDto) => IMPORT_TYPE_OPTIONS.find((t) => t.value === r.importType)?.label ?? r.importType },
              { key: "progress", label: "נרשמו", align: "end", render: (r: DataEntrySessionSummaryDto) => `${r.committed.toLocaleString("he-IL")} / ${r.rowCount.toLocaleString("he-IL")}` },
              {
                key: "status",
                label: "",
                render: (r: DataEntrySessionSummaryDto) => (r.status === "done" ? <Tag tone="success">הושלם</Tag> : r.analyzed ? <Tag tone="primary">בעבודה</Tag> : <Tag>לפני סריקה</Tag>),
              },
              { key: "updatedAt", label: "עודכן", render: (r: DataEntrySessionSummaryDto) => new Date(r.updatedAt).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" }) },
              {
                key: "del",
                label: "",
                render: (r: DataEntrySessionSummaryDto) => (
                  <span onClick={(e) => e.stopPropagation()}>
                    <IconButton icon="trash" size="sm" label="מחיקה" onClick={() => void remove(r.id)} />
                  </span>
                ),
              },
            ]}
          />
        )}
      </Panel>
      {agent ? <DataEntryAgent key={agent.sessionId ?? "new"} open sessionId={agent.sessionId} onClose={closeAgent} /> : null}
    </div>
  );
}
